import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";

import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";

import { FaCalendarPlus, FaRegCalendarAlt, FaUserPlus } from "react-icons/fa";
import { useTheme } from "../../context/ThemeContext";

import {
  updateMeetingInfo,
  getOneMeetingInfo,
} from "../../reducers/meetingSlice";
import {
  getCustomersinChunk,
  getGroups,
} from "../../reducers/customerTableSlice";
import { getIds } from "../../utils/shareUtils";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import { CustomButton } from "../../components/CustomButton";

const reminderOptions = [
  { label: "10 min", value: "10" },
  { label: "30 min", value: "30" },
  { label: "1 hour", value: "60" },
  { label: "1 day", value: "1440" },
];

const EditMeeting = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: meetingId } = useParams();
  const { currentMeeting, isLoading, error } = useSelector(
    (state) => state.meeting
  );
  const { customerData, groups, totalCustomerCount, totalGroupCount } =
    useSelector((s) => s.customerTable);
  const { theme } = useTheme();
  const toast = useRef(null);

  // --- Form State ---
  const [meetingData, setMeetingData] = useState({
    title: "",
    organizer: "",
    date: "",
    time: "",
    reminder: "",
    url: "",
  });

  // --- Customer/Group Selection State ---
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [showAddCustomerSection, setShowAddCustomerSection] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerTab, setCustomerTab] = useState("individual");

  // --- Data Fetching and Population ---
  useEffect(() => {
    if (meetingId) dispatch(getOneMeetingInfo(meetingId));
  }, [dispatch, meetingId]);

  useEffect(() => {
    if (currentMeeting && currentMeeting.data) {
      const data = currentMeeting.data;
      setMeetingData({
        title: data.meetingName,
        organizer: data.organizerName,
        date: data.date,
        time: data.time,
        reminder: data.reminderTime,
        url: data.url,
      });
      setSelectedCustomers(data.customers || []);
      setSelectedGroups([]); // Assuming groups are not loaded in the view, but keeping the state ready
    }
  }, [currentMeeting]);

  // Fetch customers/groups for the "add" section
  useEffect(() => {
    if (showAddCustomerSection) {
      if (
        customerData.length < totalCustomerCount ||
        customerData.length === 0
      ) {
        const fetchAllChunks = async () => {
          let page = 0;
          let done = false;
          while (!done) {
            const result = await dispatch(
              getCustomersinChunk({ page, limit: 100, globalFilterValue: "" })
            );
            page++;
            const receivedCount = result.payload?.rows?.length || 0;
            if (receivedCount < 100) done = true;
          }
        };
        fetchAllChunks();
      }
      if (!groups.length && totalGroupCount === 0) {
        dispatch(getGroups({ page: 0, limit: 1000, globalFilterValue: "" }));
      }
    }
  }, [
    dispatch,
    showAddCustomerSection,
    customerData.length,
    totalCustomerCount,
    groups.length,
    totalGroupCount,
  ]);

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMeetingData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    const payload = {
      meetingName: meetingData.title,
      organizerName: meetingData.organizer,
      customerIds: getIds(selectedCustomers),
      groupIds: selectedGroups.map((g) => g.id),
      date: meetingData.date,
      time: meetingData.time,
      reminderTime: meetingData.reminder,
      url: meetingData.url,
      // Template IDs are sent directly from the original data
      bookingTemplateId: currentMeeting.data.bookingTemplate?.id || null,
      reminderTemplateId: currentMeeting.data.reminderTemplate?.id || null,
      confirmationTemplateId:
        currentMeeting.data.confirmationTemplate?.id || null,
    };

    dispatch(updateMeetingInfo({ id: meetingId, data: payload }))
      .unwrap()
      .then(() => {
        toast.current.show({
          severity: "success",
          detail: "Meeting updated successfully",
        });
        setTimeout(() => navigate("/meeting"), 1000);
      })
      .catch((err) => {
        console.error("Failed to update meeting ❌", err);
        toast.current.show({
          severity: "error",
          detail: err.response?.data?.error || "Failed to update meeting.",
        });
      });
  };

  const toggleCustomer = (cust) => {
    setSelectedCustomers((prev) =>
      prev.find((x) => x.id === cust.id)
        ? prev.filter((x) => x.id !== cust.id)
        : [...prev, cust]
    );
  };
  const toggleGroup = (grp) => {
    setSelectedGroups((prev) =>
      prev.find((x) => x.id === grp.id)
        ? prev.filter((x) => x.id !== grp.id)
        : [...prev, grp]
    );
  };

  const filteredCustomers = customerData.filter(
    (c) =>
      c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
      c.phone?.toString().includes(customerSearch)
  );
  const filteredGroups = groups.filter((g) =>
    g.groupName?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  // --- Render Helpers ---
  if (isLoading) return <div>Loading meeting data...</div>;
  if (error)
    return (
      <div style={{ color: "red" }}>
        Error: {error.error || "Failed to load meeting."}
      </div>
    );
  if (!currentMeeting || !currentMeeting.data)
    return <div>Meeting not found.</div>;

  const cardStyle = {
    marginBottom: "1.25rem",
    border: "1px solid #ddd",
    borderRadius: "6px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    padding: "1.25rem",
    marginRight: "40rem",
  };
  const sectionTitleStyle = {
    fontSize: "1.2rem",
    fontWeight: "600",
    marginBottom: "1rem",
    color: "#333",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };
  const inputStyle = {
    width: "100%",
    padding: "0.75rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "1rem",
  };

  const headerRow = (iconJSX, heading) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: "1rem",
      }}
    >
      {iconJSX}
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{heading}</h3>
    </div>
  );

  const getCustomerSummary = (customers) => {
    if (!customers || customers.length === 0) return "No customers selected";
    const shown = customers.slice(0, 3).map((c) => c.name);
    const remaining = customers.length - 3;
    return remaining > 0
      ? `${shown.join(", ")}, +${remaining}`
      : shown.join(", ");
  };

  return (
    <div
      style={{
        padding: "2rem",
        fontFamily: "Inter, sans-serif",
        background: theme.base_background,
        color: theme.text_primary,
        minHeight: "100vh",
      }}
    >
      <Toast ref={toast} />
      <CustomBreadcrumb
        home={{ label: "Meeting Scheduler", url: "/meeting" }}
        itemslist={[
          { label: "Meetings", url: "/meeting" },
          { label: "Edit Meeting", url: `/meeting/edit/${meetingId}` },
        ]}
        style={{ borderRadius: "8px", marginBottom: "1.5rem" }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: ".5rem",
          marginBottom: "1.5rem",
          marginTop: "1.5rem",
        }}
      >
        <FaCalendarPlus size={24} color={theme.primary} />
        <h2 style={{ margin: 0 }}>Edit Meeting Information</h2>
        <div style={{ marginLeft: "57rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <Button
              label="Cancel"
              outlined
              style={{
                color: theme.text_secondary,
                fontSize: "0.7rem",
                cursor: "pointer",
              }}
              onClick={() => navigate(-1)}
            />
            <CustomButton
              label="Save Changes"
              style={{
                backgroundColor: theme.primary,
                border: "none",
                color: theme.sideBarText,
              }}
              onClick={handleSave}
            />
          </div>
        </div>
      </div>

      {/* --- Meeting Details --- */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>
          <FaRegCalendarAlt /> Meeting Details
        </div>
        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label htmlFor="title">Meeting title*</label>
            <InputText
              id="title"
              name="title"
              value={meetingData.title}
              onChange={handleInputChange}
            />
          </div>
          <div className="field col-6">
            <label htmlFor="date">Date*</label>
            <Calendar
              id="date"
              value={meetingData.date ? new Date(meetingData.date) : null}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: "date",
                    value: e.value.toISOString().split("T")[0],
                  },
                })
              }
              showIcon
              dateFormat="dd/mm/yy"
            />
          </div>
          <div className="field col-6">
            <label htmlFor="time">Time*</label>
            <Calendar
              id="time"
              value={
                meetingData.time
                  ? new Date(`1970-01-01T${meetingData.time}`)
                  : null
              }
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: "time",
                    value: e.value.toLocaleTimeString("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    }),
                  },
                })
              }
              timeOnly
              hourFormat="12"
              showIcon
            />
          </div>
          <div className="field col-6">
            <label htmlFor="organizer">Organizer*</label>
            <InputText
              id="organizer"
              name="organizer"
              value={meetingData.organizer}
              onChange={handleInputChange}
            />
          </div>
          <div className="field col-6">
            <label htmlFor="reminder">Reminder Time*</label>
            <Dropdown
              id="reminder"
              name="reminder"
              value={meetingData.reminder}
              options={reminderOptions}
              onChange={(e) =>
                handleInputChange({
                  target: { name: "reminder", value: e.value },
                })
              }
              placeholder="Select reminder time"
            />
          </div>
          <div className="field col-12">
            <label htmlFor="url">Meeting URL</label>
            <InputText
              id="url"
              name="url"
              value={meetingData.url}
              onChange={handleInputChange}
              placeholder="https://meet.google.com/..."
            />
          </div>
        </div>
      </div>

      {/* --- Selected Customers --- */}
      <div style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <div style={sectionTitleStyle}>
            <i className="pi pi-users" /> Selected Customers (
            {selectedCustomers.length})
          </div>
          <Button
            icon="pi pi-plus"
            label="Add Customer"
            onClick={() => setShowAddCustomerSection(!showAddCustomerSection)}
            style={{
              backgroundColor: theme.primary,
              border: "none",
              color: theme.sideBarText,
            }}
          />
        </div>
        <div
          style={{
            padding: "1rem",
            backgroundColor: "#f1f1f1",
            borderRadius: "6px",
            color: "#555",
          }}
        >
          {getCustomerSummary(selectedCustomers)}
        </div>
      </div>

      {/* --- Add Customer Section (Conditional) --- */}
      {showAddCustomerSection && (
        <div style={cardStyle}>
          <div style={sectionTitleStyle}>
            <FaUserPlus /> Add Participants
          </div>
          <div
            style={{
              display: "flex",
              background: theme.sideBarText,
              padding: "0.5rem 0.75rem",
              borderRadius: 6,
              border: `1px solid ${theme.border}`,
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <i className="pi pi-search" style={{ marginRight: 6 }} />
            <InputText
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Search customers or groups"
              style={{ border: "none", flex: 1 }}
            />
          </div>
          <div
            style={{
              display: "flex",
              background: theme.secondary,
              borderRadius: 8,
              border: `1px solid ${theme.primary}`,
              overflow: "hidden",
              marginBottom: "1rem",
            }}
          >
            {["individual", "groups"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setCustomerTab(t)}
                style={{
                  flex: 1,
                  padding: "0.6rem 1rem",
                  background:
                    customerTab === t ? theme.secondary : theme.base_background,
                  color: customerTab === t ? theme.primary : theme.text_primary,
                  fontWeight: customerTab === t ? 600 : 500,
                  border: "none",
                }}
              >
                {t === "individual" ? "Individual Customers" : "Groups"}
              </button>
            ))}
          </div>
          <div
            style={{
              maxHeight: 240,
              overflowY: "auto",
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
            }}
          >
            {(() => {
              const listToRender =
                customerTab === "individual"
                  ? filteredCustomers
                  : filteredGroups;
              const uniqueList = [
                ...new Map(
                  listToRender.map((item) => [item.id, item])
                ).values(),
              ];

              return uniqueList.map((item) => (
                <div
                  key={`${customerTab}-${item.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0.75rem 0.9rem",
                    borderBottom: `1px solid ${theme.border}`,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      customerTab === "individual"
                        ? selectedCustomers.some((x) => x.id === item.id)
                        : selectedGroups.some((x) => x.id === item.id)
                    }
                    onChange={() =>
                      customerTab === "individual"
                        ? toggleCustomer(item)
                        : toggleGroup(item)
                    }
                    style={{ marginRight: 12 }}
                  />
                  <label style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>
                      {customerTab === "individual"
                        ? item.name
                        : item.groupName}
                    </div>
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: theme.text_secondary,
                      }}
                    >
                      {customerTab === "individual"
                        ? item.phone
                        : `${item.customerIds?.length || 0} members`}
                    </div>
                  </label>
                </div>
              ));
            })()}
          </div>
          <div style={{ marginTop: "1rem", textAlign: "right" }}>
            <Button
              label="Done"
              onClick={() => setShowAddCustomerSection(false)}
            />
          </div>
        </div>
      )}

      {/* --- Selected Templates (Updated to Match Step3Review) --- */}
      {/* <Card
        style={cardStyle}
        header={headerRow(
          <FaRegCalendarAlt size={18} color="#3B82F6" />,
          "Selected Templates"
        )}
      >
        {[
          {
            key: "booking",
            title: "Booking",
            badgeColor: "#2563eb",
            badgeBg: "#eff6ff",
            borderColor: "#2563eb",
          },
          {
            key: "reminder",
            title: "Reminder",
            badgeColor: "#d97706",
            badgeBg: "#fff7ed",
            borderColor: "#d97706",
          },
          {
            key: "confirmation",
            title: "Confirmation",
            badgeColor: "#0f766e",
            badgeBg: "#ecfdf5",
            borderColor: "#0f766e",
          },
        ].map((tpl, idx) => {
          const template = currentMeeting.data[`${tpl.key}Template`];
          return (
            <div
              key={tpl.key}
              style={{
                marginTop: idx === 0 ? "1rem" : "1.25rem",
                borderLeft: `4px solid ${tpl.borderColor}`,
                paddingLeft: "1rem",
                background: "#f8f9fa",
              }}
            >
              <Tag
                value={tpl.title}
                severity="info"
                style={{
                  background: tpl.badgeBg,
                  color: tpl.badgeColor,
                  fontWeight: 700,
                  marginBottom: ".5rem",
                }}
              />
              {template ? (
                <>
                  <div style={{ fontWeight: 600 }}>
                    {template.header || "-"}
                  </div>

                  <div style={{ fontWeight: 600, whiteSpace: "pre-wrap" }}>
                    {template.body || "-"}
                  </div>

                  {template.footer && <div>{template.footer}</div>}
                </>
              ) : (
                <span>Not selected</span>
              )}
            </div>
          );
        })}
      </Card> */}
    </div>
  );
};

export default EditMeeting;
